<script lang="ts">
  import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
  import { PrinterType, PrintPluginStatus } from '../../modules/PrintService';
  import { IPrintSettings } from '../../interfaces/IPrintSettings';
  import { PrintSource, TicketPrintSetting } from '../../enums/PrintEnum';
  import { vueData } from '../../VueData';
  import { Globals } from '../../modules/Globals';
  import EscPosEncoder, { PrintFormat } from '../../lib/print/EscPosEncoder';
  import { usePrinting } from '@/composition/use-printing';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { IPrinter } from '@/interfaces/IPrinter';
  import { encode } from 'iconv-lite';
  import { IPrintingSettings } from '@/interfaces/IPrintingSettings';
  import StickerSettingsModal from './sticker/StickerSettingsModal.vue';
  import { LabelPrinter } from '@/lib/print/LabelPrinter';
  import {
    Label,
    PrintDensity,
    PrintDensityName,
    Spacing,
    Text,
    FontFamily,
    FontFamilyName,
    Alignment,
    AlignmentValue,
  } from 'jszpl';

  @Component
  export default class NativePrintSettingsModal extends Vue {
    printTicketOptions = [
      { value: TicketPrintSetting.Manual, text: 'Manualmente' },
      { value: TicketPrintSetting.OnReceive, text: 'Al recibir pedido' },
      { value: TicketPrintSetting.OnConfirm, text: 'Al confirmar un pedido' },
      {
        value: TicketPrintSetting.OnFinish,
        text: 'Al establecer pedido como finalizado',
      },
      {
        value: TicketPrintSetting.OnPaid,
        text: 'Al establecer pedido como pagado',
      },
    ];
    printSources = [
      { value: PrintSource.ThisDevice, text: 'este punto de venta' },
      {
        value: PrintSource.ThisDeviceAndOnline,
        text: 'este punto de venta y pedidos online',
      },
      { value: PrintSource.AnyDevice, text: 'cualquier punto de venta' },
    ];
    printFormats = [
      { value: PrintFormat.Generic, text: 'Normal' },
      { value: PrintFormat.GenericB, text: 'Normal B' },
    ];
    codepages = [
      'cp437',
      'cp737',
      'cp850',
      'cp775',
      'cp852',
      'cp855',
      'cp857',
      'cp858',
      'cp860',
      'cp861',
      'cp862',
      'cp863',
      'cp864',
      'cp865',
      'cp866',
      'cp869',
      'cp936',
      'cp949',
      'cp950',
      'cp1252',
      'iso88596',
      'shiftjis',
      'windows1250',
      'windows1251',
      'windows1252',
      'windows1253',
      'windows1254',
      'windows1255',
      'windows1256',
      'windows1257',
      'windows1258',
    ];
    testingPrinter = null;
    printing = usePrinting();
    restaurantStore = useRestaurantStore();

    async created() {}

    save() {}

    @Watch('connected', { immediate: true })
    async onConnectedChange(connected: boolean) {
      if (connected) {
        await this.reloadPrinters();
      }
    }

    get connected() {
      return this.printing.status.value === PrintPluginStatus.Connected;
    }

    get printers() {
      return this.printing.printers.value;
    }

    async reloadPrinters() {
      try {
        await this.printing.getPrinters('usb');
      } catch (error) {
        console.log(error);
      }
    }

    get selectedStickerPrinterId(): number | string | null {
      return this.printing.stickerPrinter.value?.id ?? null;
    }

    set selectedStickerPrinterId(val: number | string) {
      if (val) {
      const printer = this.printers?.find((p) => p.id === val);
      this.printing.setStickerPrinter(printer);
      } else {
        this.printing.setStickerPrinter(null);
      }
    }

    get printStations() {
      return this.restaurantStore.printStations;
    }

    getComandaPrinter(stationId: number): IPrinter | null {
      return this.printing.comandaPrintersByStation.value[stationId] ?? null;
    }

    getComandaPrinterId(stationId: number): number | string | null {
      return this.getComandaPrinter(stationId)?.id ?? null;
    }

    setComandaPrinterId(stationId: number, val: number | string | null) {
      if (val) {
        const printer = this.printers?.find((p) => p.id === val);
        this.printing.setComandaPrinter(stationId, printer ?? null);
      } else {
        this.printing.setComandaPrinter(stationId, null);
      }
    }

    get firstComandaPrinter(): IPrinter | null {
      for (const station of this.printStations) {
        const printer = this.getComandaPrinter(station.stationId);
        if (printer) return printer;
      }
      return null;
    }

    get selectedTicketPrinterId(): number | string | null {
      return this.printing.ticketPrinter.value?.id ?? null;
    }

    set selectedTicketPrinterId(val: number | string) {
      if (val) {
        const printer = this.printers?.find((p) => p.id === val);
        this.printing.setTicketPrinter(printer);
      } else {
        this.printing.setTicketPrinter(null);
      }
    }

    async test(printer: IPrinter, settings: IPrintingSettings) {
      try {
        const e = new EscPosEncoder()
          .codepage(settings.encoding)
          .align('center')
          .line('Prueba de impresión')
          .line('AÁáEÉéIÍíOÓóUÚúNÑñ$€')
          .line('https://queresto.com')
          .qrcode('https://queresto.com')
          .newline()
          .newline()
          .newline()
          .cut('partial');
        const data = e.encode();

        await this.printing.printByteArray(printer, data);
      } catch (error) {
        console.log(error);
      }
    }

    async testSticker(printer: IPrinter, settings: IPrintingSettings) {
      try {
        const settings = LabelPrinter.loadSettings();

        const label = new Label();
        label.printDensity = new PrintDensity(
          PrintDensityName[settings.printDensity]
        );
        label.width = settings.width;
        label.height = settings.height;
        label.padding = new Spacing(
          settings.padding.left,
          settings.padding.top,
          settings.padding.right,
          settings.padding.bottom
        );

        const text = new Text();
        label.content.push(text);
        let fontFamily = FontFamilyName.F;
        switch (settings.fontSize) {
          case 'xs':
            fontFamily = FontFamilyName.B;
            break;
          case 's':
            fontFamily = FontFamilyName.D;
            break;
          case 'm':
            fontFamily = FontFamilyName.F;
            break;
          case 'l':
            fontFamily = FontFamilyName.E;
            break;
        }

        text.fontFamily = new FontFamily(fontFamily);
        text.text =
          'Prueba de Impresión\nEsta es una prueba de impresión del sistema de etiquetas de QueResto\nhttps://queresto.com';
        text.verticalAlignment = new Alignment(AlignmentValue.Start);
        text.horizontalAlignment = new Alignment(AlignmentValue.Start);

        const zpl = label.generateZPL();
        await this.printing.printZpl(printer, zpl);
      } catch (error) {
        console.log(error);
      }
    }

    async testEncodings(printer: IPrinter) {
      try {
        this.testingPrinter = printer;

        let e = new EscPosEncoder().align('center').line('Prueba de impresión');
        let data = e.encode();

        await this.printing.printByteArray(printer, data);

        for (const codepage of this.codepages) {
          const e = new EscPosEncoder()
            .codepage(codepage)
            .align('center')
            .line(codepage + ' - ÁáÉéÍíÓóÚúÑñ$€');
          const data = e.encode();

          await this.printing.printByteArray(printer, data);
        }

        e = new EscPosEncoder()
          .align('center')
          .newline()
          .newline()
          .newline()
          .cut('partial');
        data = e.encode();

        await this.printing.printByteArray(printer, data);
      } catch (error) {
        console.log(error);
      } finally {
        this.testingPrinter = null;
      }
    }

    async setupSticker() {
      this.$modal.show(
        StickerSettingsModal,
        {},
        {
          name: 'sticker-settings',
          adaptive: true,
          classes: 'dialog--height-limit',
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
          width: 640,
          clickToClose: false,
        },
        {}
      );
    }

    get outdatedVersion() {
      return this.printing.isOutdatedVersion.value;
    }

    get pluginDownloadUrl() {
      return `${Globals.uploadsPath}/plugin2/QueRestoPrintBridge-Setup.exe`;
    }
  }
</script>

<template>
  <v-card class="native-print-settings">
    <v-card-title class="align-center tw-space-x-3">
      <span>Impresión</span>
      <div v-if="connected && !outdatedVersion" class="top-actions">
        <v-btn
          color="primary"
          @click="reloadPrinters"
          small
          rounded
          elevation="0"
        >
          <v-icon small class="mr-1">mdi-reload</v-icon>
          Actualizar impresoras
        </v-btn>
      </div>
    </v-card-title>
    <v-card-text class="settings-inner">
      <template v-if="!connected">
        <div class="plugin-not-installed">
          <p class="grey--text mb-0">
            Para poder imprimir tickets y comandas, necesita instalar el plugin
            para impresión QueResto que le permite a la plataforma interactuar
            con su impresora. <b>Si ya tiene instalado el plugin, probablemente no esté ejecutandose o tenga una versión antigua.</b><br />

            <span class="pb-4 d-block" style="color: #b38663"
              ><b>IMPORTANTE:</b> Si al abrir el instalador Windows muestra una
              advertencia, presionar <b>"Mas información"</b> y luego
              <b>"Ejecutar de todos modos"</b>.</span
            >

            <span class="text-center d-block">
              <v-btn
                small
                rounded
                color="info"
                :href="pluginDownloadUrl"
                ><v-icon small class="mr-1">mdi-download</v-icon> Descargar
                plugin para Windows</v-btn
              >
            </span>
            <v-divider class="my-4"></v-divider>
            <div class="tw-flex tw-space-x-2 tw-items-center mt-4">
              Si ya está instalado y ejecutandose, intente
              <v-btn
                color="primary"
                class="ml-1"
                small
                rounded
                @click="printing.connect"
                :loading="printing.status.value === 1"
                >Conectar</v-btn
              >
            </div>
          </p>

          <v-alert
            type="warning"
            border="left"
            colored-border
            elevation="2"
            class="mt-4"
          >
            <div class="tw-font-semibold tw-mb-2">
              <v-icon small class="mr-1">mdi-information</v-icon>
              ¿No puede conectar desde hace poco?
            </div>
            <div class="tw-text-sm">
              Chrome ha lanzado recientemente una actualización que solicita permiso para acceder a la red local. Si clickeó <b>"Bloquear"</b>, el navegador impedirá la conexión con el plugin de impresión.
            </div>
            <div class="tw-text-sm tw-mt-3">
              <b>Para solucionarlo:</b>
              <ol class="tw-mt-2 tw-ml-4">
                <li>Haga clic en el ícono de <b>configuración</b> ubicado inmediatamente a la izquierda de la dirección en la barra de direcciones (donde aparece "queresto.com/su-restaurante" o "bistrify.com/su-restaurante")</li>
                <li>Active la opción <b>"Acceso a la red local"</b></li>
                <li>Actualice esta página y vuelva a intentar conectar</li>
              </ol>
            </div>
          </v-alert>
        </div>
      </template>
      <template v-else-if="outdatedVersion">
        <div class="plugin-not-installed">
          <p class="mb-0">
            Para poder imprimir tickets y comandas, <b>necesita actualizar el plugin</b>
            para impresión QueResto a la última versión.<br />

            <span class="pb-4 d-block" style="color: #b38663"
              ><b>IMPORTANTE:</b> Si al abrir el instalador Windows muestra una
              advertencia, presionar <b>"Mas información"</b> y luego
              <b>"Ejecutar de todos modos"</b>.</span
            >

            <span class="text-center d-block">
              <v-btn
                small
                rounded
                color="info"
                :href="pluginDownloadUrl"
                ><v-icon small class="mr-1">mdi-download</v-icon> Descargar
                plugin para Windows</v-btn
              >
            </span>

            <br />
            <b>Si ya instaló la nueva versión</b>, es probable que la anterior siga ejecutandose. Pruebe desinstalando todas las versiones anteriores y luego instale la nueva versión, o reinicie su PC y vuelva a intentar.
          </p>
        </div>
      </template>
      <template v-else>
        <div>
          <v-row
            v-for="station in printStations"
            :key="station.stationId"
            align="center"
          >
            <v-col cols="12" md="2">Comandera {{ station.name }}</v-col>
            <v-col cols="12" md="3">
              <v-select
                :value="getComandaPrinterId(station.stationId)"
                :items="printers"
                hide-details
                label="Elija impresora"
                outlined
                dense
                item-value="id"
                item-text="name"
                clearable
                @change="setComandaPrinterId(station.stationId, $event)"
              />
            </v-col>
            <v-col cols="12" md="2">
              <v-btn
                color="primary"
                @click="
                  test(
                    getComandaPrinter(station.stationId),
                    printing.comandaPrintingSettings.value
                  )
                "
                :disabled="!getComandaPrinter(station.stationId)"
                elevation="0"
              >
                Probar
              </v-btn>
            </v-col>
          </v-row>
          <v-row align="center">
            <v-col cols="12" md="2" class="tw-flex tw-flex-col">
              <span>Codificación comanderas</span>
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="printing.comandaPrintingSettings.value.encoding"
                :items="codepages"
                hide-details
                label="Codificación"
                outlined
                dense
                return-object
              />
            </v-col>
            <v-col cols="12" md="2">
              <v-btn
                color="primary"
                @click="testEncodings(firstComandaPrinter)"
                :disabled="!firstComandaPrinter || testingPrinter"
                elevation="0"
              >
                Probar
              </v-btn>
            </v-col>
          </v-row>
          <v-row align="center">
            <v-col cols="12" md="2">Tickets / Recibos</v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="selectedTicketPrinterId"
                :items="printers"
                hide-details
                label="Elija impresora"
                outlined
                dense
                item-value="id"
                item-text="name"
                clearable
              />
            </v-col>
            <v-col cols="12" md="2">
              <v-btn
                color="primary"
                @click="
                  test(
                    printing.ticketPrinter.value,
                    printing.ticketPrintingSettings.value
                  )
                "
                :disabled="!printing.ticketPrinter.value"
                elevation="0"
              >
                Probar
              </v-btn>
            </v-col>
            <v-col cols="12" md="4">
              <div class="tw-flex tw-space-x-2 tw-items-center">
                <div>
                  <v-select
                    v-model="printing.ticketPrintingSettings.value.encoding"
                    :items="codepages"
                    hide-details
                    label="Codificación"
                    outlined
                    dense
                    return-object
                  />
                </div>
                <div>
                  <v-btn
                    color="primary"
                    @click="testEncodings(printing.ticketPrinter.value)"
                    :disabled="!printing.ticketPrinter.value || testingPrinter"
                    elevation="0"
                  >
                    Probar
                  </v-btn>
                </div>
              </div>
            </v-col>
          </v-row>
          <v-row align="center">
            <v-col cols="12" md="2" class="tw-flex tw-flex-col">
              <span>Etiquetas / Stickers</span>
              <span class="tw-text-xs">Zebra o compatibles</span>
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="selectedStickerPrinterId"
                :items="printers"
                hide-details
                label="Elija impresora"
                outlined
                dense
                item-value="id"
                item-text="name"
                clearable
              />
            </v-col>
            <v-col cols="12" md="2">
              <v-btn
                color="primary"
                @click="
                  testSticker(
                    printing.stickerPrinter.value,
                    printing.stickerPrintingSettings.value
                  )
                "
                :disabled="!printing.stickerPrinter.value"
                elevation="0"
              >
                Probar
              </v-btn>
            </v-col>
            <v-col cols="12" md="2">
              <v-btn color="primary" @click="setupSticker" elevation="0">
                Configurar
              </v-btn>
            </v-col>
          </v-row>
        </div>
        <h4>Cuando imprimir...</h4>
        <div>
          <v-row align="center">
            <v-col cols="12" md="2">Comandas</v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="printing.comandaPrintingSettings.value.trigger"
                :items="printTicketOptions"
                hide-details
                label="Elija condición"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="printing.comandaPrintingSettings.value.source"
                :items="printSources"
                hide-details
                label="Pedidos de..."
                outlined
                dense
              />
            </v-col>
          </v-row>
          <v-row align="center">
            <v-col cols="12" md="2">Tickets</v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="printing.ticketPrintingSettings.value.trigger"
                :items="printTicketOptions"
                hide-details
                label="Elija condición"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="printing.ticketPrintingSettings.value.source"
                :items="printSources"
                hide-details
                label="Pedidos de..."
                outlined
                dense
              />
            </v-col>
          </v-row>
          <v-row align="center">
            <v-col cols="12" md="2">Stickers</v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="printing.stickerPrintingSettings.value.trigger"
                :items="printTicketOptions"
                hide-details
                label="Elija condición"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="printing.stickerPrintingSettings.value.source"
                :items="printSources"
                hide-details
                label="Pedidos de..."
                outlined
                dense
              />
            </v-col>
          </v-row>
        </div>
      </template>
    </v-card-text>
    <v-card-actions class="justify-end">
      <v-btn text @click="$emit('close')">Cerrar</v-btn>
    </v-card-actions>
  </v-card>
</template>

<style lang="scss" scoped>
  @import '@/assets/styles/titles.scss';
  .settings-inner {
    @apply tw-space-y-2;

    .top-actions {
      @apply tw-flex tw-justify-end tw-space-x-2;
    }
  }
</style>
